import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Checkbox,
  Button,
  Grid,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Link,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import SyncIcon from '@mui/icons-material/Sync';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MovieIcon from '@mui/icons-material/Movie';
import StarIcon from '@mui/icons-material/Star';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import VerifiedIcon from '@mui/icons-material/Verified';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useToetsNavigatie from '../../hooks/useToetsNavigatie';
import { VIDEO_CATEGORIEEN, CATEGORIE_OVERIG, toonCategorie } from '../../config/videoCategorieen';
import { authFetch, getAuthHeaders } from '../../utils/authHelpers';
import { stuurBestand } from '../../utils/opnameUpload';
import { bevestig } from '../../utils/bevestig';
import DeelDialog from './DeelDialog';
import GedeeldeLinksDialog from './GedeeldeLinksDialog';
import VerzamelingDialog from './VerzamelingDialog';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import PageHeader from '../../ui-components/PageHeader';

const API_BASE = `${AI_ENGINE_URL}/api/v1/inspiration`;

const CATEGORIES = VIDEO_CATEGORIEEN;

export default function Inspiration() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [editVideo, setEditVideo] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validateResult, setValidateResult] = useState(null);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [unavailableCount, setUnavailableCount] = useState(0);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [playerVideo, setPlayerVideo] = useState(null);
  const [beheerAnchor, setBeheerAnchor] = useState(null);
  // Persoonlijke favorieten (wiki 10 sep): één gedeelde bibliotheek, per persoon
  // een merkteken. Plus filteren op tag/categorie, want zoeken op tekst vindt
  // niet wat je met één klik wilt afbakenen.
  const [favorieten, setFavorieten] = useState(new Set());
  const [alleenFavorieten, setAlleenFavorieten] = useState(false);
  const [alleenZelfUploaden, setAlleenZelfUploaden] = useState(false);
  // Selecteren om met een klant te delen (Joris, 11 sep): doorsturen mag,
  // kijken altijd, downloaden per link instelbaar.
  const [selectie, setSelectie] = useState(new Set());
  const [linksOpen, setLinksOpen] = useState(false);
  // Verzamelingen: benoemde lijstjes van het team, naast de persoonlijke favoriet.
  const [verzamelingen, setVerzamelingen] = useState([]);
  const [verzameling, setVerzameling] = useState(null);       // actieve filter (object)
  const [verzamelingAnchor, setVerzamelingAnchor] = useState(null);
  const [verzamelingOpen, setVerzamelingOpen] = useState(false); // toevoegen-dialoog
  const [deelVerzameling, setDeelVerzameling] = useState(null);  // delen met meegroeiende link
  const haalVerzamelingen = useCallback(async () => {
    try {
      const r = await authFetch(`${API_BASE}/collections`);
      if (!r.ok) return;
      const d = await r.json();
      setVerzamelingen(d.collections || []);
      setVerzameling((huidig) => (huidig ? (d.collections || []).find((c) => c.id === huidig.id) || null : null));
    } catch { /* lijst blijft zoals hij was */ }
  }, []);
  useEffect(() => { haalVerzamelingen(); }, [haalVerzamelingen]);
  const verwijderVerzameling = async (c) => {
    const ok = await bevestig({
      titel: `Verzameling "${c.naam}" verwijderen?`,
      tekst: 'De video\'s zelf blijven staan. Een deellink die aan deze verzameling hing, toont daarna wat erin zat op het moment van delen.',
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return;
    try {
      const r = await authFetch(`${API_BASE}/collections/${c.id}`, { method: 'DELETE' });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${r.status}`); }
      toast.success('Verzameling verwijderd');
      if (verzameling?.id === c.id) setVerzameling(null);
      haalVerzamelingen();
    } catch (e) { toast.error(`Verwijderen mislukt: ${e.message}`); }
  };
  const haalUitVerzameling = async (ids) => {
    if (!verzameling) return;
    try {
      await Promise.all(ids.map((id) => authFetch(`${API_BASE}/collections/${verzameling.id}/videos/${id}`, { method: 'DELETE' })));
      toast.success(`${ids.length} uit "${verzameling.naam}" gehaald`);
      setSelectie(new Set());
      await haalVerzamelingen();
      laad(query);
    } catch (e) { toast.error(`Weghalen mislukt: ${e.message}`); }
  };
  const [deelOpen, setDeelOpen] = useState(false);
  const [tagFilter, setTagFilter] = useState(null);
  const [categorieFilter, setCategorieFilter] = useState(null);
  const debounceRef = useRef(null);

  // Fetch all items (no search query)
  // Eén laadfunctie voor bladeren én zoeken. De server filtert en telt; het
  // scherm zeeft niets meer zelf (analyse 12 sep: favoriet #250 was onvindbaar
  // omdat alleen de eerste 100 rijen werden opgehaald).
  const [facets, setFacets] = useState({ categories: {}, tags: [] });
  const [tellingen, setTellingen] = useState({ favorites: 0, needsUpload: 0 });
  const [meerLaden, setMeerLaden] = useState(false);
  const PAGINA = 60;

  const bouwParams = useCallback((q, extra = {}) => {
    const p = new URLSearchParams();
    p.set('limit', String(PAGINA));
    p.set('offset', String(extra.offset || 0));
    p.set('include_unavailable', String(Boolean(showUnavailable)));
    if (q && q.trim()) p.set('q', q.trim());
    if (categorieFilter) p.set('category', categorieFilter);
    if (tagFilter) p.set('tag', tagFilter);
    if (alleenFavorieten) p.set('favorites', 'true');
    if (alleenZelfUploaden) p.set('needs_upload', 'true');
    if (verzameling) p.set('collection', String(verzameling.id));
    return p;
  }, [showUnavailable, categorieFilter, tagFilter, alleenFavorieten, alleenZelfUploaden, verzameling]);

  const laad = useCallback(async (q, { offset = 0, stil = false } = {}) => {
    if (offset === 0 && !stil) setLoading(true); else setMeerLaden(true);
    setError(null);
    try {
      const resp = await authFetch(`${API_BASE}/?${bouwParams(q, { offset })}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setResults((oud) => (offset === 0 ? (data.results || []) : [...oud, ...(data.results || [])]));
      setTotal(data.total || 0);
      setUnavailableCount(data.unavailable_count || 0);
      setFacets(data.facets || { categories: {}, tags: [] });
      setTellingen({ favorites: data.favorites_count || 0, needsUpload: data.needs_upload_count || 0 });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false); setMeerLaden(false);
    }
  }, [bouwParams]);

  const fetchAll = useCallback(() => laad(query), [laad, query]);
  const searchVideos = useCallback((q) => laad(q), [laad]);

  // Eén laadmoment voor alles: typen (kort uitgesteld), een filterklik en de
  // eerste keer openen. `laad` wisselt mee met de filters, dus die triggeren hier ook.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { laad(query); }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [query, laad]);

  const haalFavorieten = useCallback(async () => {
    try {
      const resp = await authFetch(`${API_BASE}/favorites`);
      if (!resp.ok) return;
      const data = await resp.json();
      setFavorieten(new Set(data.video_ids || []));
    } catch { /* favorieten zijn een extraatje, geen blokkade */ }
  }, []);

  const wisselFavoriet = useCallback(async (video) => {
    // Meteen omzetten: één klik moet direct zichtbaar zijn, ook op een trage lijn.
    setFavorieten((oud) => {
      const nieuw = new Set(oud);
      if (nieuw.has(video.id)) nieuw.delete(video.id); else nieuw.add(video.id);
      return nieuw;
    });
    try {
      const resp = await authFetch(`${API_BASE}/${video.id}/favorite`, { method: 'POST' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    } catch {
      setFavorieten((oud) => {
        const nieuw = new Set(oud);
        if (nieuw.has(video.id)) nieuw.delete(video.id); else nieuw.add(video.id);
        return nieuw;
      });
    }
  }, []);

  useEffect(() => { haalFavorieten(); }, [haalFavorieten]);

  // Losse link naar één video (Joris, 11 sep: "ik wil ook losse linkjes naar
  // inspiratie intern kunnen delen"). ?video=<id> opent hem meteen.
  useEffect(() => {
    const id = Number(new URLSearchParams(window.location.search).get('video'));
    if (!id || !results.length) return;
    const gevonden = results.find((v) => v.id === id);
    if (gevonden) setPlayerVideo((huidig) => huidig || gevonden);
  }, [results]);

  // De server filtert; hier alleen nog doorgeven wat er is.
  const zichtbaar = results;

  // Bladeren in het spelervenster (←/→): door de lijst zoals hij in beeld staat.
  // Aan het eind van wat geladen is haalt hij de volgende pagina op.
  const spelerIndex = playerVideo ? results.findIndex((v) => v.id === playerVideo.id) : -1;
  const naarSpeler = (stap) => {
    const doel = results[spelerIndex + stap];
    if (doel) setPlayerVideo(doel);
    else if (stap > 0 && results.length < total && !meerLaden) laad(query, { offset: results.length });
  };
  const zelfUploadenAantal = tellingen.needsUpload;
  const categorieFacet = facets.categories || {};

  // Poll sync status
  const pollSyncStatus = useCallback(async () => {
    try {
      const resp = await authFetch(`${API_BASE}/sync/status`);
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.running) {
        setSyncing(true);
        setSyncResult({ synced: data.synced, skipped: data.skipped, errors: data.errors, running: true });
      } else {
        setSyncing(false);
        if (data.synced > 0 || data.errors > 0) {
          setSyncResult({ synced: data.synced, skipped: data.skipped, errors: data.errors });
          // Refresh list
          if (query.trim()) { searchVideos(query); } else { fetchAll(); }
        }
        if (data.last_error) setError(data.last_error);
      }
      return data.running;
    } catch { return false; }
  }, [query, searchVideos, fetchAll]);

  // Sync from Teams
  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const resp = await authFetch(`${API_BASE}/sync`, { method: 'POST' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      // Start polling
      const interval = setInterval(async () => {
        const running = await pollSyncStatus();
        if (!running) clearInterval(interval);
      }, 3000);
    } catch (e) {
      setError(`Sync mislukt: ${e.message}`);
      setSyncing(false);
    }
  };

  // Check if sync is already running on load
  useEffect(() => {
    pollSyncStatus().then(running => {
      if (running) {
        const interval = setInterval(async () => {
          const stillRunning = await pollSyncStatus();
          if (!stillRunning) clearInterval(interval);
        }, 3000);
      }
    });
  }, [pollSyncStatus]);

  // Poll validation status
  const pollValidateStatus = useCallback(async () => {
    try {
      const resp = await authFetch(`${API_BASE}/validate/status`);
      if (!resp.ok) return false;
      const data = await resp.json();
      if (data.running) {
        setValidating(true);
        setValidateResult({
          checked: data.checked,
          total: data.total,
          unavailable: data.unavailable,
          restored: data.restored || 0,
          thumbnails_updated: data.thumbnails_updated,
          running: true,
        });
      } else {
        setValidating(false);
        if (data.checked > 0) {
          setValidateResult({
            checked: data.checked,
            unavailable: data.unavailable,
            restored: data.restored || 0,
            thumbnails_updated: data.thumbnails_updated,
          });
          // Refresh list after validation
          if (query.trim()) { searchVideos(query); } else { fetchAll(); }
        }
      }
      return data.running;
    } catch { return false; }
  }, [query, searchVideos, fetchAll]);

  // Start validation
  const handleValidate = async () => {
    setValidating(true);
    setValidateResult(null);
    try {
      const resp = await authFetch(`${API_BASE}/validate-all`, { method: 'POST' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      // Start polling
      const interval = setInterval(async () => {
        const running = await pollValidateStatus();
        if (!running) clearInterval(interval);
      }, 2000);
    } catch (e) {
      setError(`Validatie mislukt: ${e.message}`);
      setValidating(false);
    }
  };

  // Start media backfill (thumbnails + video backups)
  const handleBackfill = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const resp = await authFetch(`${API_BASE}/backfill-media`, { method: 'POST' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      // Poll backfill status
      const interval = setInterval(async () => {
        try {
          const statusResp = await authFetch(`${API_BASE}/backfill-media/status`);
          if (!statusResp.ok) { clearInterval(interval); setBackfilling(false); return; }
          const data = await statusResp.json();
          setBackfillResult(`${data.processed}/${data.total} verwerkt${data.errors ? `, ${data.errors} fouten` : ''}`);
          if (!data.running) {
            clearInterval(interval);
            setBackfilling(false);
            // Refresh the list to show new thumbnails
            if (query.trim()) searchVideos(query);
            else fetchAll();
          }
        } catch { clearInterval(interval); setBackfilling(false); }
      }, 3000);
    } catch (e) {
      setError(`Backfill mislukt: ${e.message}`);
      setBackfilling(false);
    }
  };

  // Update a video in the local results list
  const handleVideoUpdated = (updatedVideo) => {
    setResults(prev => prev.map(v => v.id === updatedVideo.id ? updatedVideo : v));
  };

  // Add a new video to the list
  const handleVideoAdded = (newVideo) => {
    setResults(prev => [newVideo, ...prev]);
    setTotal(prev => prev + 1);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      {/* Header */}
      <PageHeader
        icon="lightbulb"
        title="Inspiratie"
        subtitle="Zoek op stijl, techniek, sfeer of onderwerp — AI vindt relevante video's"
        rightActions={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
          >
            Toevoegen
          </Button>
          {/* Beheerwerk achter één knop (Joris, 12 sep: "bovenste strakker").
              Synchroniseren, valideren en backfill zijn voor af en toe, niet
              voor iedereen die een video komt zoeken. */}
          <Button
            variant="outlined"
            color="inherit"
            onClick={(e) => setBeheerAnchor(e.currentTarget)}
            startIcon={(syncing || validating || backfilling) ? <CircularProgress size={16} /> : <SettingsIcon />}
            sx={{ textTransform: 'none' }}
          >
            Beheer
          </Button>
          <Menu anchorEl={beheerAnchor} open={Boolean(beheerAnchor)} onClose={() => setBeheerAnchor(null)}>
            <MenuItem onClick={() => { setBeheerAnchor(null); setLinksOpen(true); }}>
              <LinkIcon fontSize="small" sx={{ mr: 1.5 }} /> Mijn gedeelde selecties
            </MenuItem>
            <MenuItem disabled={syncing || validating} onClick={() => { setBeheerAnchor(null); handleSync(); }}>
              <SyncIcon fontSize="small" sx={{ mr: 1.5 }} /> {syncing ? 'Sync loopt…' : 'Sync Teams-kanaal'}
            </MenuItem>
            <MenuItem disabled={validating || syncing || backfilling} onClick={() => { setBeheerAnchor(null); handleValidate(); }}>
              <VerifiedIcon fontSize="small" sx={{ mr: 1.5 }} /> {validating ? 'Valideren…' : 'Beschikbaarheid valideren'}
            </MenuItem>
            <MenuItem disabled={backfilling || syncing || validating} onClick={() => { setBeheerAnchor(null); handleBackfill(); }}>
              <CloudDownloadIcon fontSize="small" sx={{ mr: 1.5 }} /> {backfilling ? (backfillResult || 'Backfill loopt…') : 'Thumbnails en kopieën aanvullen'}
            </MenuItem>
          </Menu>
        </Box>
        }
      />

      {/* Sync result */}
      {syncResult && (
        <Alert severity={syncResult.running ? "info" : "success"} sx={{ mb: 2 }} onClose={syncResult.running ? undefined : () => setSyncResult(null)}>
          {syncResult.running
            ? `Sync bezig... ${syncResult.synced} nieuw, ${syncResult.skipped} overgeslagen`
            : `Sync voltooid: ${syncResult.synced} nieuw, ${syncResult.skipped} overgeslagen`}
          {syncResult.errors > 0 && `, ${syncResult.errors} fouten`}
        </Alert>
      )}

      {/* Validation result */}
      {validateResult && (
        <Alert
          severity={validateResult.running ? "info" : validateResult.unavailable > 0 ? "warning" : "success"}
          sx={{ mb: 2 }}
          onClose={validateResult.running ? undefined : () => setValidateResult(null)}
        >
          {validateResult.running
            ? `Validatie bezig... ${validateResult.checked}/${validateResult.total} gecontroleerd`
            : `Validatie voltooid: ${validateResult.checked} gecontroleerd, ${validateResult.unavailable} onbeschikbaar${validateResult.restored > 0 ? `, ${validateResult.restored} hersteld` : ''}, ${validateResult.thumbnails_updated} thumbnails bijgewerkt`}
        </Alert>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search bar */}
      <TextField
        fullWidth
        placeholder="Zoek op stijl, techniek, sfeer... bijv. 'energieke sportfilm' of 'mooi kleurgebruik'"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            backgroundColor: 'background.paper',
          },
        }}
      />

      {/* Video's die onze server niet mocht downloaden — YouTube weigert
          datacenter-IP's. Op je eigen laptop lukt het bijna altijd wel, dus
          vragen we het aan wie hem toevoegde (Joris, 11 sep). */}
      {zelfUploadenAantal > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={(
            <Button size="small" onClick={() => setAlleenZelfUploaden((v) => !v)} sx={{ textTransform: 'none' }}>
              {alleenZelfUploaden ? 'Alles tonen' : 'Laat zien'}
            </Button>
          )}>
          {zelfUploadenAantal === 1
            ? 'Eén video konden we niet zelf downloaden.'
            : `${zelfUploadenAantal} video's konden we niet zelf downloaden.`}
          {' '}Open hem, kies <b>Download video</b> en daarna <b>Upload video</b> — vanaf je eigen
          laptop lukt het meestal wel.
        </Alert>
      )}

      {/* Zwevende actiebalk: blijft in beeld terwijl je verder scrolt en aanvinkt
          (Beeldbank-patroon). Alles op deze pagina in één keer erbij kan ook. */}
      {selectie.size > 0 && (
        <Box sx={{
          position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 1200,
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
          bgcolor: 'grey.900', color: 'common.white', borderRadius: 999, boxShadow: 6,
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', pl: 0.5 }}>
            {selectie.size} geselecteerd
          </Typography>
          {results.some((v) => !selectie.has(v.id)) && (
            <Button size="small" color="inherit" sx={{ textTransform: 'none', opacity: 0.85 }}
              onClick={() => setSelectie(new Set(results.map((v) => v.id)))}>
              Alles in beeld
            </Button>
          )}
          <Button size="small" color="inherit" sx={{ textTransform: 'none', opacity: 0.85 }}
            onClick={() => setSelectie(new Set())}>
            Wissen
          </Button>
          {verzameling ? (
            <Button size="small" color="inherit" sx={{ textTransform: 'none', opacity: 0.85 }}
              onClick={() => haalUitVerzameling([...selectie])}>
              Uit verzameling
            </Button>
          ) : null}
          <Button size="small" color="inherit" variant="outlined" onClick={() => setVerzamelingOpen(true)}
            sx={{ textTransform: 'none', borderRadius: 999, borderColor: 'rgba(255,255,255,0.4)' }}>
            In verzameling…
          </Button>
          <Button size="small" variant="contained" color="primary" onClick={() => setDeelOpen(true)}
            sx={{ textTransform: 'none', borderRadius: 999 }}>
            Deel met klant
          </Button>
        </Box>
      )}

      {/* Filters: favorieten en tags (wiki 10 sep) */}
      {results.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <Chip
            label={verzameling ? `${verzameling.naam} · ${verzameling.aantal}` : `Verzamelingen${verzamelingen.length ? ` · ${verzamelingen.length}` : ''}`}
            icon={<CollectionsBookmarkIcon sx={{ fontSize: 16 }} />}
            color={verzameling ? 'secondary' : 'default'}
            variant={verzameling ? 'filled' : 'outlined'}
            onClick={(e) => setVerzamelingAnchor(e.currentTarget)}
            onDelete={verzameling ? () => setVerzameling(null) : undefined}
            size="small"
          />
          <Menu anchorEl={verzamelingAnchor} open={Boolean(verzamelingAnchor)} onClose={() => setVerzamelingAnchor(null)}>
            {verzamelingen.length === 0 && (
              <MenuItem disabled>Nog geen verzamelingen — vink video's aan en kies "In verzameling…"</MenuItem>
            )}
            {verzamelingen.map((c) => (
              <MenuItem key={c.id} selected={verzameling?.id === c.id}
                onClick={() => { setVerzamelingAnchor(null); setVerzameling(c); setSelectie(new Set()); }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Typography variant="body2">{c.naam}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.aantal} video{c.aantal === 1 ? '' : "'s"}{c.door ? ` · ${c.door.split('@')[0]}` : ''}</Typography>
                </Box>
              </MenuItem>
            ))}
            {verzameling && [
              <Divider key="d" />,
              <MenuItem key="deel" onClick={() => { setVerzamelingAnchor(null); setDeelVerzameling(verzameling); }}>
                <LinkIcon fontSize="small" sx={{ mr: 1.5 }} /> Deel "{verzameling.naam}" met een klant (link groeit mee)
              </MenuItem>,
              <MenuItem key="weg" onClick={() => { setVerzamelingAnchor(null); verwijderVerzameling(verzameling); }} sx={{ color: 'error.main' }}>
                <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} /> Verzameling verwijderen
              </MenuItem>,
            ]}
          </Menu>
          <Chip
            label={`Mijn favorieten${tellingen.favorites ? ` · ${tellingen.favorites}` : ''}`}
            icon={<StarIcon sx={{ fontSize: 16 }} />}
            color={alleenFavorieten ? 'warning' : 'default'}
            variant={alleenFavorieten ? 'filled' : 'outlined'}
            onClick={() => setAlleenFavorieten((v) => !v)}
            size="small"
          />
          {Object.entries(categorieFacet).slice(0, 8).map(([c, n]) => (
            <Chip key={`cat-${c}`} label={`${c} · ${n}`} size="small"
              color={categorieFilter === c ? 'primary' : 'default'}
              variant={categorieFilter === c ? 'filled' : 'outlined'}
              onClick={() => setCategorieFilter((huidig) => (huidig === c ? null : c))} />
          ))}
          {(facets.tags || []).slice(0, 10).map(([t, n]) => (
            <Chip key={`tag-${t}`} label={`#${t} · ${n}`} size="small"
              color={tagFilter === t ? 'primary' : 'default'}
              variant={tagFilter === t ? 'filled' : 'outlined'}
              onClick={() => setTagFilter((huidig) => (huidig === t ? null : t))} />
          ))}
          {unavailableCount > 0 && (
            <Chip size="small" variant={showUnavailable ? 'filled' : 'outlined'}
              color={showUnavailable ? 'warning' : 'default'}
              label={showUnavailable ? `Incl. ${unavailableCount} onbeschikbaar` : `${unavailableCount} onbeschikbaar`}
              onClick={() => setShowUnavailable(!showUnavailable)} />
          )}
          {(tagFilter || categorieFilter || alleenFavorieten || alleenZelfUploaden || verzameling) && (
            <Chip label="Filters wissen" size="small"
              onClick={() => { setTagFilter(null); setCategorieFilter(null); setAlleenFavorieten(false); setAlleenZelfUploaden(false); setVerzameling(null); }} />
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {results.length < total ? `${results.length} van ${total}` : `${total}`}
            {query.trim() ? ' treffers' : " video's"}
          </Typography>
        </Box>
      )}

      {/* Results grid */}
      {loading ? (
        <Grid container spacing={2}>
          {[...Array(8)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : zichtbaar.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <MovieIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {query.trim() ? 'Geen resultaten gevonden' : 'Nog geen inspiratievideo\'s'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {query.trim()
              ? 'Probeer een andere zoekterm'
              : 'Klik op "Sync Teams" om berichten op te halen of voeg handmatig toe'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {zichtbaar.map((video) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
              <VideoCard video={video} onEdit={setEditVideo} onPlay={setPlayerVideo}
                favoriet={favorieten.has(video.id)} onFavoriet={wisselFavoriet}
                geselecteerd={selectie.has(video.id)}
                onSelecteer={(v) => setSelectie((oud) => {
                  const nieuw = new Set(oud);
                  if (nieuw.has(v.id)) nieuw.delete(v.id); else nieuw.add(v.id);
                  return nieuw;
                })} />
            </Grid>
          ))}
        </Grid>
      )}
      {!loading && results.length < total && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button variant="outlined" disabled={meerLaden} sx={{ textTransform: 'none' }}
            onClick={() => laad(query, { offset: results.length })}>
            {meerLaden ? <CircularProgress size={18} /> : `Meer laden (${total - results.length})`}
          </Button>
        </Box>
      )}

      {deelOpen && (
        <DeelDialog videoIds={[...selectie]} onClose={() => setDeelOpen(false)} />
      )}
      {verzamelingOpen && (
        <VerzamelingDialog videoIds={[...selectie]} verzamelingen={verzamelingen}
          onKlaar={() => { setSelectie(new Set()); haalVerzamelingen(); }}
          onClose={() => setVerzamelingOpen(false)} />
      )}
      {deelVerzameling && (
        <DeelDialog videoIds={deelVerzameling.videoIds} verzameling={deelVerzameling}
          onClose={() => setDeelVerzameling(null)} />
      )}
      {linksOpen && (
        <GedeeldeLinksDialog onClose={() => setLinksOpen(false)} />
      )}

      {/* Edit Dialog */}
      {editVideo && (
        <EditDialog
          video={editVideo}
          onClose={() => setEditVideo(null)}
          onSaved={handleVideoUpdated}
          onDeleted={(id) => {
            setResults((oud) => oud.filter((v) => v.id !== id));
            setTotal((t) => Math.max(0, t - 1));
          }}
        />
      )}

      {/* Add Dialog */}
      {addOpen && (
        <AddDialog
          onClose={() => setAddOpen(false)}
          onAdded={handleVideoAdded}
        />
      )}

      {/* Video Player Dialog */}
      {playerVideo && (
        <VideoPlayerDialog
          key={playerVideo.id}
          video={playerVideo}
          deelLink={`${window.location.origin}/inspiration?video=${playerVideo.id}`}
          onClose={() => setPlayerVideo(null)}
          onVorige={spelerIndex > 0 ? () => naarSpeler(-1) : undefined}
          onVolgende={spelerIndex >= 0 && (spelerIndex < results.length - 1 || results.length < total) ? () => naarSpeler(1) : undefined}
          positie={spelerIndex >= 0 ? { nr: spelerIndex + 1, van: Math.max(total, results.length) } : null}
          onBackupDone={(videoId, blobUrl) => {
            // Update local state so video plays immediately
            setPlayerVideo(prev => prev ? { ...prev, backup_blob_url: blobUrl, backup_status: 'uploaded' } : null);
            setResults(prev => prev.map(v => v.id === videoId ? { ...v, backup_blob_url: blobUrl, backup_status: 'uploaded' } : v));
          }}
        />
      )}
    </Box>
  );
}

// Extract YouTube video ID from various URL formats
const getYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Extract Vimeo video ID
const getVimeoId = (url) => {
  if (!url) return null;
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
};

// Extract TikTok video ID
const getTikTokId = (url) => {
  if (!url) return null;
  const match = url.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)/);
  return match ? match[1] : null;
};

// Detect platform from URL
const getPlatform = (url) => {
  if (!url) return 'unknown';
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
  if (url.includes('vimeo.com')) return 'vimeo';
  if (url.includes('tiktok.com')) return 'tiktok';
  if (url.includes('instagram.com')) return 'instagram';
  return 'other';
};

// Get thumbnail URL - prefer Azure blob (always available), fallback to external
const getThumbnailUrl = (video) => {
  // Prefer Azure blob thumbnail (persistent, never breaks)
  if (video.thumbnail_blob_url) return video.thumbnail_blob_url;

  // Fallback to stored external thumbnail
  if (video.thumbnail_url) return video.thumbnail_url;

  const url = video.video_url || '';

  // YouTube thumbnail (direct URL, no API needed)
  const ytId = getYouTubeId(url);
  if (ytId) return `https://img.youtube.com/vi/${ytId}/mqdefault.jpg`;

  // For Vimeo/TikTok - thumbnail should be fetched from backend (oEmbed)
  // Return null if not stored, frontend will show placeholder
  return null;
};

function VideoCard({ video, onEdit, onPlay, favoriet = false, onFavoriet,
  geselecteerd = false, onSelecteer }) {
  const tags = Array.isArray(video.tags) ? video.tags : [];
  const techniques = Array.isArray(video.techniques) ? video.techniques : [];
  const thumbnailUrl = getThumbnailUrl(video);
  const platform = getPlatform(video.video_url);
  const [thumbnailError, setThumbnailError] = useState(false);
  const isUnavailable = video.is_available === false;

  const handleOpen = () => {
    onPlay(video);
  };

  // Extract short domain from URL
  const getShortUrl = (url) => {
    if (!url) return '';
    try {
      const u = new URL(url);
      return u.hostname.replace('www.', '');
    } catch {
      return url.substring(0, 30);
    }
  };

  // Platform colors and labels
  const platformStyles = {
    youtube: { color: '#FF0000', label: 'YouTube' },
    vimeo: { color: '#1AB7EA', label: 'Vimeo' },
    tiktok: { color: '#000000', label: 'TikTok' },
    instagram: { color: '#E4405F', label: 'Instagram' },
    other: { color: '#6366f1', label: 'Link' },
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        position: 'relative',
        // Rustiger raster (Joris, 11 sep): vinkje, ster en potlood verschijnen
        // pas bij hover. Staat er iets aan (geselecteerd, favoriet), dan blijft
        // dat natuurlijk wél zichtbaar.
        '& .kaart-knop': { opacity: 0, transition: 'opacity .15s ease' },
        '&:hover .kaart-knop': { opacity: 1 },
        '& .kaart-knop.aan': { opacity: 1 },
        transition: 'box-shadow 0.2s, transform 0.2s',
        opacity: isUnavailable ? 0.6 : 1,
        border: isUnavailable ? '2px solid #f59e0b' : 'none',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Selecteren voor een klantselectie: linksboven, weg van de ster. */}
      {onSelecteer && (
        <Tooltip title={geselecteerd ? 'Uit de selectie halen' : 'Aan de selectie voor een klant toevoegen'}>
          <Checkbox
            size="small"
            className={`kaart-knop${geselecteerd ? ' aan' : ''}`}
            checked={geselecteerd}
            onClick={(e) => { e.stopPropagation(); onSelecteer(video); }}
            sx={{ position: 'absolute', top: 8, left: 8, zIndex: 3,
              bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1, p: 0.35,
              '&:hover': { bgcolor: '#fff' } }}
          />
        </Tooltip>
      )}

      {/* Favoriet: rechtsboven, boven de thumbnail zodat hij ook zonder plaatje
          bereikbaar blijft (wiki 10 sep — eigen favorieten). */}
      {onFavoriet && (
        <Tooltip title={favoriet ? 'Uit je favorieten halen' : 'Bij je favorieten zetten'}>
          <IconButton
            size="small"
            className={`kaart-knop${favoriet ? ' aan' : ''}`}
            onClick={(e) => { e.stopPropagation(); onFavoriet(video); }}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 3,
              bgcolor: 'rgba(255,255,255,0.88)', '&:hover': { bgcolor: '#fff' } }}
          >
            {favoriet
              ? <StarIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
              : <StarBorderIcon sx={{ fontSize: 18, color: '#64748b' }} />}
          </IconButton>
        </Tooltip>
      )}

      {/* Thumbnail */}
      {thumbnailUrl && !thumbnailError ? (
        <Box
          onClick={handleOpen}
          sx={{
            position: 'relative',
            paddingTop: '56.25%', // 16:9 aspect ratio
            backgroundColor: 'grey.100',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            overflow: 'hidden',
            cursor: 'pointer',
            '&:hover .play-overlay': {
              opacity: 1,
            },
          }}
        >
          <Box
            component="img"
            src={thumbnailUrl}
            alt={video.video_title || 'Video thumbnail'}
            onError={() => setThumbnailError(true)}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          {/* Play button overlay */}
          <Box
            className="play-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.3)',
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PlayArrowIcon sx={{ color: 'primary.main', fontSize: 32, ml: 0.3 }} />
            </Box>
          </Box>
          {/* Category badge — alleen bij hover; de categorie filter je bovenaan
              en staat in het detail (Joris, 11 sep: "mag nog rustiger"). */}
          {toonCategorie(video.category) && (
            <Chip
              label={video.category}
              size="small"
              className="kaart-knop"
              sx={{
                position: 'absolute',
                top: 8,
                // Naast het selectievakje, niet eronder (Joris, 12 sep: "gaat over tag heen").
                left: onSelecteer ? 44 : 8,
                fontSize: '0.7rem',
                height: 22,
                backgroundColor: 'rgba(255,255,255,0.9)',
                fontWeight: 500,
              }}
            />
          )}
          {/* Unavailable badge */}
          {isUnavailable && (
            <Chip
              icon={<BrokenImageIcon sx={{ fontSize: 14 }} />}
              label="Onbeschikbaar"
              size="small"
              color="warning"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                fontSize: '0.65rem',
                height: 22,
                fontWeight: 600,
              }}
            />
          )}
          {/* Backup indicator — alleen bij hover: in het raster zei dit vinkje
              niets over de video zelf. */}
          {video.backup_blob_url && false && (
            <Tooltip title="Video backup opgeslagen" arrow>
              <CloudDoneIcon sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                fontSize: 20,
                color: '#fff',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
              }} />
            </Tooltip>
          )}
          {/* Platform badge — neutraal en alleen bij hover; de gekleurde pillen
              trokken meer aandacht dan de beelden zelf. */}
          <Chip
            label={platformStyles[platform]?.label || 'Link'}
            size="small"
            className="kaart-knop"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              fontSize: '0.65rem',
              height: 20,
              backgroundColor: 'rgba(15,23,42,0.65)',
              color: '#fff',
              fontWeight: 600,
            }}
          />
        </Box>
      ) : (
        /* Color header + platform when no thumbnail */
        <Box
          onClick={handleOpen}
          sx={{
            position: 'relative',
            height: 80,
            background: getCategoryColor(video.category),
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            cursor: video.video_url ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {thumbnailError && (
            <Tooltip title="Thumbnail kon niet geladen worden - video mogelijk verwijderd">
              <BrokenImageIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 32 }} />
            </Tooltip>
          )}
          {!thumbnailError && (
            <MovieIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 32 }} />
          )}
          {/* Platform badge */}
          <Chip
            label={platformStyles[platform]?.label || 'Link'}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              fontSize: '0.65rem',
              height: 20,
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: platformStyles[platform]?.color || '#6366f1',
              fontWeight: 600,
            }}
          />
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1, pt: 1.5, display: 'flex', flexDirection: 'column' }}>
        {/* Title + actions */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.3,
              flexGrow: 1,
              cursor: 'pointer',
            }}
            onClick={handleOpen}
          >
            {video.video_title || 'Onbekend'}
          </Typography>
          <Box className="kaart-knop" sx={{ display: 'flex', flexShrink: 0, ml: 0.5 }}>
            <Tooltip title="Bewerken">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(video); }} sx={{ p: 0.3 }}>
                <EditIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              </IconButton>
            </Tooltip>
            {video.video_url && (
              <Tooltip title="Openen in nieuw tabblad">
                <IconButton size="small" onClick={handleOpen} sx={{ p: 0.3 }}>
                  <OpenInNewIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* URL link */}
        {video.video_url && (
          <Link
            href={video.video_url}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            className="kaart-knop"
            sx={{ fontSize: '0.7rem', color: 'text.disabled', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <LinkIcon sx={{ fontSize: 12 }} />
            {getShortUrl(video.video_url)}
          </Link>
        )}

        {/* Category - only show if no thumbnail (otherwise shown on thumbnail) */}
        {!thumbnailUrl && toonCategorie(video.category) && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {video.category}
          </Typography>
        )}

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{
          mb: 1.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          lineHeight: 1.4,
          fontSize: '0.8rem',
        }}>
          {video.description || video.style_notes || ''}
        </Typography>

        {/* Tags — alleen bij hover. In het raster wonnen ze het van de beelden;
            filteren doe je bovenaan en in het detail staan ze allemaal. */}
        <Box className="kaart-knop" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {tags.slice(0, 3).map((tag, i) => (
            <Chip
              key={i}
              label={tag}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          ))}
          {tags.length > 3 && (
            <Chip label={`+${tags.length - 3}`} size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
          )}
        </Box>

        {/* Techniques as subtle chips */}
        {techniques.length > 0 && false && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {techniques.slice(0, 3).map((t, i) => (
              <Chip
                key={i}
                label={t}
                size="small"
                sx={{
                  fontSize: '0.65rem',
                  height: 20,
                  backgroundColor: 'rgba(99, 102, 241, 0.08)',
                  color: 'primary.main',
                }}
              />
            ))}
          </Box>
        )}

        {/* Wie en wanneer — één lichte regel bij hover; in het raster telde het
            niet mee bij het kiezen (Joris, 11 sep). */}
        <Box className="kaart-knop" sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto', pt: 0.5 }}>
          <Typography variant="caption" color="text.disabled" noWrap sx={{ maxWidth: '60%' }}>
            {video.author_name || ''}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {video.posted_at ? new Date(video.posted_at).toLocaleDateString('nl-NL') : ''}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// Get embeddable URL for YouTube/Vimeo (for videos without blob backup)
const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytId = getYouTubeId(url);
  if (ytId) return `https://www.youtube.com/embed/${ytId}?autoplay=1`;
  const vimeoId = getVimeoId(url);
  if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
  return null;
};

function VideoPlayerDialog({ video, onClose, onBackupDone, deelLink, onVorige, onVolgende, positie }) {
  useToetsNavigatie({ onVorige, onVolgende });
  const embedUrl = getEmbedUrl(video.video_url);
  const platform = getPlatform(video.video_url);
  const tags = Array.isArray(video.tags) ? video.tags : [];
  const [videoError, setVideoError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Onze eigen kopie gaat vóór de embed. De embed won eerder altijd, ook als
  // YouTube 'm weigerde af te spelen (fout 153, embedden uitgezet) — dan keek je
  // naar een foutmelding terwijl de video gewoon in onze opslag stond (Joris,
  // 11 sep). Valt onze kopie weg, dan is de embed het vangnet.
  const hasBlobVideo = !!video.backup_blob_url && !videoError;
  const useBlob = hasBlobVideo;
  const useEmbed = !useBlob && !!embedUrl;

  const platformLabel = platform === 'instagram' ? 'Instagram'
    : platform === 'tiktok' ? 'TikTok'
    : platform === 'youtube' ? 'YouTube'
    : platform === 'vimeo' ? 'Vimeo'
    : 'originele site';

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5 }}>
        <Typography variant="h6" fontWeight={600} noWrap sx={{ flex: 1, mr: 2 }}>
          {video.video_title || 'Video'}
        </Typography>
        {(onVorige || onVolgende || positie) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: 1 }}>
            <Tooltip title="Vorige (←)"><span>
              <IconButton size="small" disabled={!onVorige} onClick={onVorige} aria-label="Vorige"><ChevronLeftIcon /></IconButton>
            </span></Tooltip>
            {positie && (
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                {positie.nr} / {positie.van}
              </Typography>
            )}
            <Tooltip title="Volgende (→)"><span>
              <IconButton size="small" disabled={!onVolgende} onClick={onVolgende} aria-label="Volgende"><ChevronRightIcon /></IconButton>
            </span></Tooltip>
          </Box>
        )}
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Video player */}
        {useBlob ? (
          <Box sx={{ width: '100%', backgroundColor: '#000' }}>
            <video
              src={video.backup_blob_url}
              controls
              autoPlay
              onError={() => setVideoError(true)}
              style={{ width: '100%', maxHeight: '70vh', display: 'block' }}
            />
          </Box>
        ) : useEmbed ? (
          <Box sx={{ position: 'relative', paddingTop: '56.25%', backgroundColor: '#000' }}>
            <iframe
              src={embedUrl}
              title={video.video_title || 'Video'}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: '100%', border: 'none',
              }}
            />
          </Box>
        ) : (
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', py: 6, backgroundColor: '#f9fafb',
          }}>
            {video.video_url && (
              <Button
                variant="contained"
                size="large"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open(video.video_url, '_blank', 'noopener,noreferrer')}
                sx={{ mb: 1 }}
              >
                Openen op {platformLabel}
              </Button>
            )}
            <Typography variant="caption" color="text.disabled">
              {videoError ? 'Video backup kon niet afgespeeld worden' : 'Geen inline preview beschikbaar'}
            </Typography>
          </Box>
        )}

        {/* Info below video */}
        <Box sx={{ px: 2.5, py: 2 }}>
          {/* Link naar déze video, om intern te delen (Joris, 11 sep). */}
          {deelLink && (
            <Button size="small" startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', mb: 1 }}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(deelLink);
                  toast.success('Link naar deze video gekopieerd');
                } catch { toast.error('Kopiëren lukte niet'); }
              }}>
              Link kopiëren voor collega's
            </Button>
          )}

          {/* Original link — always visible */}
          {video.video_url && (
            <Link
              href={video.video_url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem', mb: 1.5 }}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
              Bekijk op {platformLabel} — {video.video_url.length > 80 ? video.video_url.substring(0, 80) + '...' : video.video_url}
            </Link>
          )}

          {/* Reacties: één lijst per video, geen threads (Joris, 11 sep). */}
          <Reacties videoId={video.id} />

          {/* Download / Upload buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              {video.video_url && (!video.backup_blob_url || video.backup_status === 'failed' || videoError) && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={downloading ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                  disabled={downloading || uploading}
                  onClick={async () => {
                    setDownloading(true);
                    try {
                      const resp = await authFetch(`${API_BASE}/${video.id}/download-backup`, { method: 'POST' });
                      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                      // Poll for completion
                      const poll = setInterval(async () => {
                        try {
                          const r = await authFetch(`${API_BASE}/${video.id}`);
                          if (!r.ok) return;
                          const v = await r.json();
                          if (v.backup_status === 'uploaded') {
                            clearInterval(poll);
                            setDownloading(false);
                            if (onBackupDone) onBackupDone(video.id, v.backup_blob_url);
                          } else if (v.backup_status === 'failed') {
                            clearInterval(poll);
                            setDownloading(false);
                          }
                        } catch { /* keep polling */ }
                      }, 5000);
                      // Timeout after 10 min
                      setTimeout(() => { clearInterval(poll); setDownloading(false); }, 600000);
                    } catch {
                      setDownloading(false);
                    }
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  {downloading ? 'Downloaden...' : 'Download video'}
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                component="label"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                disabled={downloading || uploading}
                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
              >
                {uploading ? 'Uploaden...' : 'Upload video'}
                <input
                  type="file"
                  hidden
                  accept=".mp4,.mov,.mkv,.avi,.webm"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploading(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const resp = await authFetch(`${API_BASE}/${video.id}/upload-backup`, {
                        method: 'POST',
                        body: formData,
                      });
                      if (!resp.ok) {
                        const err = await resp.json().catch(() => ({}));
                        throw new Error(err.detail || `HTTP ${resp.status}`);
                      }
                      const data = await resp.json();
                      if (onBackupDone) onBackupDone(video.id, data.blob_url);
                    } catch (err) {
                      toast.error(`Upload mislukt: ${err.message}`);
                    } finally {
                      setUploading(false);
                      e.target.value = '';
                    }
                  }}
                />
              </Button>
          </Box>

          {/* Description */}
          {video.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {video.description}
            </Typography>
          )}

          {/* Original Teams message */}
          {video.message_body && (
            <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.5 }}>
                Origineel bericht
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {video.message_body}
              </Typography>
            </Box>
          )}

          {/* Meta row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            {toonCategorie(video.category) && (
              <Chip label={video.category} size="small" sx={{ fontWeight: 600 }} />
            )}
            {tags.map((tag, i) => (
              <Chip key={i} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
            ))}
            {video.author_name && (
              <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                Gedeeld door {video.author_name}
                {video.posted_at && ` — ${new Date(video.posted_at).toLocaleDateString('nl-NL')}`}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ video, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    video_title: video.video_title || '',
    description: video.description || '',
    category: video.category || CATEGORIE_OVERIG,
    tags: (Array.isArray(video.tags) ? video.tags : []).join(', '),
    techniques: (Array.isArray(video.techniques) ? video.techniques : []).join(', '),
    style_notes: video.style_notes || '',
  });
  const [saving, setSaving] = useState(false);
  // Video vervangen of alsnog uploaden, en de video weggooien (Joris, 10 sep).
  const [uploaden, setUploaden] = useState(false);
  const [voortgang, setVoortgang] = useState(0);
  const [verwijderen, setVerwijderen] = useState(false);
  const bestandRef = useRef(null);

  const uploadVideo = async (bestand) => {
    setUploaden(true);
    setVoortgang(0);
    try {
      const auth = await getAuthHeaders();
      const bijgewerkt = await stuurBestand({
        url: `${API_BASE}/${video.id}/upload-backup`,
        bestand,
        veld: 'file',
        headers: { Authorization: auth.Authorization },
        onVoortgang: setVoortgang,
      });
      onSaved(bijgewerkt);
      toast.success('Video opgeslagen in onze eigen opslag');
    } catch (e) {
      toast.error(`Uploaden mislukt: ${e.message}`);
    } finally {
      setUploaden(false);
      setVoortgang(0);
    }
  };

  const verwijder = async () => {
    if (!(await bevestig({
      titel: 'Video verwijderen?',
      tekst: `"${video.video_title || 'Deze video'}" verdwijnt uit de bibliotheek, ook bij collega's die hem als favoriet hebben. De opgeslagen kopie en de still worden opgeruimd.`,
      knop: 'Verwijderen',
    }))) return;
    setVerwijderen(true);
    try {
      const resp = await authFetch(`${API_BASE}/${video.id}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      onDeleted?.(video.id);
      toast.success('Video verwijderd');
      onClose();
    } catch (e) {
      toast.error(`Verwijderen mislukt: ${e.message}`);
    } finally {
      setVerwijderen(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        video_title: form.video_title,
        description: form.description,
        category: form.category,
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
        techniques: form.techniques.split(',').map(s => s.trim()).filter(Boolean),
        style_notes: form.style_notes,
      };
      const resp = await authFetch(`${API_BASE}/${video.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const updated = await resp.json();
      onSaved(updated);
      onClose();
    } catch (e) {
      toast.error(`Opslaan mislukt: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Inspiratie bewerken</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {video.video_url && (
          <Link href={video.video_url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
            {video.video_url}
          </Link>
        )}
        <TextField
          label="Titel"
          value={form.video_title}
          onChange={e => setForm(f => ({ ...f, video_title: e.target.value }))}
          fullWidth
          size="small"
        />
        <TextField
          label="Beschrijving"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          fullWidth
          multiline
          rows={3}
          size="small"
        />
        <TextField
          label="Categorie"
          value={form.category}
          onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
          fullWidth
          size="small"
          select
          SelectProps={{ native: true }}
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </TextField>
        <TextField
          label="Tags (kommagescheiden)"
          value={form.tags}
          onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
          fullWidth
          size="small"
          helperText="Bijv: energiek, sport, drone"
        />
        <TextField
          label="Technieken (kommagescheiden)"
          value={form.techniques}
          onChange={e => setForm(f => ({ ...f, techniques: e.target.value }))}
          fullWidth
          size="small"
          helperText="Bijv: slow-motion, timelapse, drone"
        />
        <TextField
          label="Stijlnotities"
          value={form.style_notes}
          onChange={e => setForm(f => ({ ...f, style_notes: e.target.value }))}
          fullWidth
          size="small"
          multiline
          rows={2}
        />
        {/* Eigen kopie: uploaden of vervangen. Speelt daarna af zonder YouTube. */}
        <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 2 }}>
          <input ref={bestandRef} type="file" hidden
            accept="video/mp4,video/quicktime,video/x-matroska,video/webm,.mp4,.mov,.mkv,.webm"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadVideo(f); }} />
          <Button size="small" variant="outlined" disabled={uploaden}
            startIcon={uploaden ? <CircularProgress size={14} /> : <CloudUploadIcon />}
            onClick={() => bestandRef.current?.click()}>
            {uploaden ? `Uploaden… ${voortgang}%` : video.backup_blob_url ? 'Video vervangen' : 'Videobestand uploaden'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {video.backup_blob_url
              ? 'Er staat al een eigen kopie; een nieuwe upload vervangt die.'
              : 'mp4, mov, mkv of webm, max 500 MB — komt in onze eigen opslag en speelt af zonder YouTube.'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Button color="error" onClick={verwijder} disabled={verwijderen || uploaden}
          startIcon={verwijderen ? <CircularProgress size={14} /> : <DeleteIcon />}>
          Verwijderen
        </Button>
        <Box>
          <Button onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || uploaden}>
            {saving ? <CircularProgress size={18} /> : 'Opslaan'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function AddDialog({ onClose, onAdded }) {
  const [form, setForm] = useState({
    video_url: '',
    video_title: '',
    description: '',
    category: CATEGORIE_OVERIG,
    tags: '',
  });
  const [saving, setSaving] = useState(false);
  // Twee manieren om iets in de bibliotheek te krijgen (wiki 10 sep): een link,
  // of een eigen bestand — dat laatste voor materiaal dat nergens online staat.
  const [manier, setManier] = useState('link');
  const [bestand, setBestand] = useState(null);
  const [sleept, setSleept] = useState(false);
  // Eén plek voor kiezen én slepen: een verkeerd bestandstype meldt zich meteen
  // in plaats van pas bij het uploaden.
  const kiesBestand = (f) => {
    if (!/\.(mp4|mov|mkv|webm)$/i.test(f.name || '')) {
      toast.error('Alleen mp4, mov, mkv of webm');
      return;
    }
    if (f.size > 500 * 1024 * 1024) {
      toast.error('Dit bestand is groter dan 500 MB');
      return;
    }
    setBestand(f);
  };

  const [voortgang, setVoortgang] = useState(0);
  const bestandRef = useRef(null);

  const handleAdd = async () => {
    const perBestand = manier === 'bestand';
    if (perBestand && !bestand) { toast.error('Kies eerst een videobestand'); return; }
    if (!perBestand && !form.video_url.trim()) { toast.error('Video URL is verplicht'); return; }
    setSaving(true);
    setVoortgang(0);
    try {
      const body = {
        video_url: perBestand ? '' : form.video_url.trim(),
        bron: perBestand ? 'upload' : 'link',
        video_title: form.video_title.trim() || (perBestand ? bestand.name.replace(/\.[a-z0-9]+$/i, '') : ''),
        description: form.description.trim(),
        category: form.category,
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
      };
      const resp = await authFetch(`${API_BASE}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      let created = await resp.json();

      if (perBestand) {
        // Het bestand gaat als tweede stap naar de opslag; de engine haalt er ook
        // meteen een still uit zodat de kaart niet leeg blijft. Mislukt die stap,
        // dan ruimen we het zojuist gemaakte record op — anders blijft er een lege
        // kaart in de bibliotheek staan (Codex, 11 sep).
        const auth = await getAuthHeaders();
        try {
          const geupload = await stuurBestand({
            url: `${API_BASE}/${created.id}/upload-backup`,
            bestand,
            veld: 'file',
            headers: { Authorization: auth.Authorization },
            onVoortgang: setVoortgang,
          });
          // Het uploadendpoint geeft niet altijd de hele video terug; haal 'm op
          // zodat de kaart meteen titel, tags en thumbnail heeft.
          const vers = await authFetch(`${API_BASE}/${created.id}`);
          created = vers.ok ? await vers.json() : { ...created, ...(geupload || {}) };
        } catch (uploadFout) {
          await authFetch(`${API_BASE}/${created.id}`, { method: 'DELETE' }).catch(() => {});
          throw uploadFout;
        }
      }
      onAdded(created);
      onClose();
    } catch (e) {
      toast.error(`Toevoegen mislukt: ${e.message}`);
    } finally {
      setSaving(false);
      setVoortgang(0);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Inspiratie toevoegen</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label="Link plakken" size="small" onClick={() => setManier('link')}
            color={manier === 'link' ? 'primary' : 'default'}
            variant={manier === 'link' ? 'filled' : 'outlined'} />
          <Chip label="Bestand uploaden" size="small" onClick={() => setManier('bestand')}
            color={manier === 'bestand' ? 'primary' : 'default'}
            variant={manier === 'bestand' ? 'filled' : 'outlined'} />
        </Box>
        {manier === 'link' ? (
          <TextField
            label="Video URL *"
            value={form.video_url}
            onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))}
            fullWidth
            size="small"
            placeholder="https://www.youtube.com/watch?v=..."
            helperText={'YouTube, Vimeo, Instagram, etc. Een Frame.io-link mag ook, maar daar kunnen '
              + 'wij de video niet zelf ophalen — download hem daar en gebruik "Bestand uploaden".'}
          />
        ) : (
          <Box>
            <input ref={bestandRef} type="file" accept="video/mp4,video/quicktime,video/x-matroska,video/webm,.mp4,.mov,.mkv,.webm"
              hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) kiesBestand(f); }} />
            {/* Slepen mag ook (Joris, 11 sep): je hebt de video net uit Frame.io of
                YouTube gehaald en die staat in je downloadmap. */}
            <Box
              onClick={() => !saving && bestandRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); if (!saving) setSleept(true); }}
              onDragLeave={() => setSleept(false)}
              onDrop={(e) => {
                e.preventDefault();
                setSleept(false);
                if (saving) return;
                const f = e.dataTransfer?.files?.[0];
                if (f) kiesBestand(f);
              }}
              sx={{
                border: '2px dashed', borderColor: sleept ? 'primary.main' : '#cbd5e1',
                bgcolor: sleept ? 'rgba(37,99,235,0.06)' : 'transparent',
                borderRadius: 2, p: 3, textAlign: 'center',
                cursor: saving ? 'default' : 'pointer', transition: 'all .15s ease',
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 28, color: sleept ? 'primary.main' : '#94a3b8' }} />
              <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>
                {bestand ? bestand.name : 'Sleep een videobestand hierheen'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {bestand
                  ? `${(bestand.size / 1024 / 1024).toFixed(0)} MB — klik om een ander bestand te kiezen`
                  : 'of klik om te kiezen · mp4, mov, mkv of webm, maximaal 500 MB'}
              </Typography>
              {!bestand && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  De video komt in onze eigen opslag en speelt af zonder YouTube.
                </Typography>
              )}
            </Box>
            {saving && voortgang > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Uploaden… {voortgang}%
              </Typography>
            )}
          </Box>
        )}
        <TextField
          label="Titel (optioneel — wordt automatisch opgehaald)"
          value={form.video_title}
          onChange={e => setForm(f => ({ ...f, video_title: e.target.value }))}
          fullWidth
          size="small"
        />
        <TextField
          label="Beschrijving (optioneel)"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          fullWidth
          multiline
          rows={2}
          size="small"
        />
        <TextField
          label="Categorie"
          value={form.category}
          onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
          fullWidth
          size="small"
          select
          SelectProps={{ native: true }}
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </TextField>
        <TextField
          label="Tags (kommagescheiden, optioneel)"
          value={form.tags}
          onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
          fullWidth
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button onClick={handleAdd} variant="contained"
          disabled={saving || (manier === 'link' ? !form.video_url.trim() : !bestand)}>
          {saving ? <CircularProgress size={18} /> : 'Toevoegen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function getCategoryColor(category) {
  const colors = {
    'Commercial': 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    'Short Film': 'linear-gradient(135deg, #ec4899, #f43f5e)',
    'Music Video': 'linear-gradient(135deg, #f59e0b, #f97316)',
    'Documentary': 'linear-gradient(135deg, #10b981, #059669)',
    'Corporate': 'linear-gradient(135deg, #3b82f6, #2563eb)',
    'Social Media': 'linear-gradient(135deg, #8b5cf6, #a855f7)',
    'Animation': 'linear-gradient(135deg, #14b8a6, #06b6d4)',
    'Aftermovie': 'linear-gradient(135deg, #f43f5e, #e11d48)',
    'Sport': 'linear-gradient(135deg, #22c55e, #16a34a)',
    'Fashion': 'linear-gradient(135deg, #d946ef, #a855f7)',
    'Food': 'linear-gradient(135deg, #f97316, #ea580c)',
    'Travel': 'linear-gradient(135deg, #06b6d4, #0891b2)',
    'Tutorial': 'linear-gradient(135deg, #64748b, #475569)',
  };
  return colors[category] || 'linear-gradient(135deg, #94a3b8, #64748b)';
}

/**
 * Reacties onder een inspiratievideo — zodat mensen op elkaar kunnen reageren
 * (Joris, 11 sep 2026). Plat: oudste boven, je eigen reactie kun je weghalen.
 */
function Reacties({ videoId }) {
  const [reacties, setReacties] = useState([]);
  const [tekst, setTekst] = useState('');
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    let gestopt = false;
    authFetch(`${API_BASE}/${videoId}/comments`)
      .then((r) => (r.ok ? r.json() : { reacties: [] }))
      .then((d) => { if (!gestopt) setReacties(d.reacties || []); })
      .catch(() => {});
    return () => { gestopt = true; };
  }, [videoId]);

  const plaats = async () => {
    const inhoud = tekst.trim();
    if (!inhoud) return;
    setBezig(true);
    try {
      const r = await authFetch(`${API_BASE}/${videoId}/comments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tekst: inhoud }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
      setReacties((oud) => [...oud, d.reactie]);
      setTekst('');
    } catch (e) {
      toast.error(`Plaatsen mislukt: ${e.message}`);
    } finally { setBezig(false); }
  };

  const verwijder = async (id) => {
    try {
      const r = await authFetch(`${API_BASE}/comments/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setReacties((oud) => oud.filter((x) => x.id !== id));
    } catch { toast.error('Verwijderen lukte niet'); }
  };

  return (
    <Box sx={{ mt: 1.5, mb: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary',
        textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Reacties{reacties.length ? ` (${reacties.length})` : ''}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.75 }}>
        {reacties.map((r) => (
          <Box key={r.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{r.tekst}</Typography>
              <Typography variant="caption" color="text.disabled">
                {r.auteur}{r.op ? ` · ${new Date(r.op).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}` : ''}
              </Typography>
            </Box>
            <Button size="small" onClick={() => verwijder(r.id)}
              sx={{ textTransform: 'none', minWidth: 0, color: 'text.disabled' }}>
              ✕
            </Button>
          </Box>
        ))}
        {reacties.length === 0 && (
          <Typography variant="body2" color="text.disabled">
            Nog geen reacties. Zeg wat je ervan vindt.
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <TextField size="small" fullWidth placeholder="Schrijf een reactie…" value={tekst}
          onChange={(e) => setTekst(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) plaats(); }} />
        <Button variant="contained" size="small" onClick={plaats} disabled={bezig || !tekst.trim()}
          sx={{ textTransform: 'none' }}>
          Plaatsen
        </Button>
      </Box>
    </Box>
  );
}
